import { isPlatformServer } from '@angular/common';
import type { AfterViewInit, OnInit } from '@angular/core';
import { Component, inject, Input, PendingTasks, PLATFORM_ID, ViewChild } from '@angular/core';
import { BrandConfigurationService } from '@core-mkt/services/configuration/brand-configuration.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import { ScriptLoaderService } from '@core-mkt/services/script-loader/script-loader.service';
import { TrustpilotService } from '@core-mkt/services/trustpilot/trustpilot.service';
import { VerticalService } from '@core-mkt/services/vertical/vertical.service';
import { Trustpilot } from './trustpilot';
import { getBrandSiteUrl } from '@core-mkt/services/schema/organization.schema';
import { getTrustpilotSchema } from './trustpilot.schema';

/** Upper bound on the SSR Trustpilot fetch so a stalled call can't hang the render. */
const TRUSTPILOT_SSR_TIMEOUT_MS = 5000;

@Component({
  selector: 'trustpilot',
  templateUrl: './trustpilot.component.html',
  styleUrls: ['./trustpilot.component.scss'],
  standalone: true,
  imports: [],
})
export class TrustpilotComponent implements OnInit, AfterViewInit {
  @Input() data: Trustpilot;
  @Input() lazy = true;
  @ViewChild('trustpilot') container;
  pilotType: string;
  hasLoaded = false;
  brandUrl: string;
  businessId: string;
  widgets = {
    slider: {
      template: '54ad5defc6454f065c28af8b',
      height: '240px',
    },
    grid: {
      template: '539adbd6dec7e10e686debee',
      height: '500px',
    },
    productReview: {
      template: '5717796816f630043868e2e8',
      height: '400px',
    },
    horizontal: {
      template: '5406e65db0d04a09e042d5fc',
      height: '30px',
    },
    microCombo: {
      template: '5419b6ffb0d04a076446a9af',
      height: '30px',
    },
    microTrustScore: {
      template: '5419b637fa0340045cd0c936',
      height: '30px',
    },
    mini: {
      template: '53aa8807dec7e10d38f59f32',
      height: '130px',
    },
    carousel: {
      template: '53aa8912dec7e10d38f59f36',
      height: '240px',
    },
    list: {
      template: '539ad60defb9600b94d7df2c',
      height: '500px',
      class: 'trustpilot-list-container',
    },
  };

  private bcs = inject(BrandConfigurationService);
  private sls = inject(ScriptLoaderService);
  private tps = inject(TrustpilotService);
  private vs = inject(VerticalService);
  private schemaService = inject(SchemaService);
  private env = inject(EnvService);
  private pendingTasks = inject(PendingTasks);
  private platformId = inject(PLATFORM_ID);

  constructor() {
    this.brandUrl = `https://www.trustpilot.com/review/${this.bcs.url}`;
    this.businessId = this.bcs.trustpilotId;
  }

  get trustPilotDisplayType(): object {
    const widgets = this.widgets as Record<string, { template: string; height: string; class?: string }>;
    return widgets[this.data.trustpilotDisplayType] ?? widgets.grid;
  }

  get showHideTrustpilot(): boolean {
    return this.data.showHideTrustpilot;
  }

  get dataSku(): string {
    return this.data.dataSku;
  }

  get dataName(): string {
    return this.data.dataName;
  }

  ngOnInit(): void {
    this.pilotType = 'trust-' + this.data.trustpilotDisplayType;
    this.registerSchema();
  }

  /**
   * Fetches Trustpilot brand rating during SSR and registers the rich
   * EducationalOrganization+AggregateRating node. PendingTasks keeps Angular's
   * isStable from firing until the async fetch resolves, so finalize() in
   * template-single sees the rich node and skips the universal fallback.
   *
   * Registers for every template: Trustpilot's client-injected Product schema is a
   * different entity and must not suppress the SSR org rating (EPS-4844).
   */
  private registerSchema(): void {
    // SSR-only. SchemaService is a browser no-op, and getBrandStarsRating() sends
    // the Trustpilot apikey header — running it client-side would both leak the key
    // and do pointless network/PendingTasks work. Never execute off the server.
    if (!isPlatformServer(this.platformId)) return;
    if (!this.data?.showHideTrustpilot) return;
    if (!(this.env.get.brandConfig.trustpilotId?.length > 0)) return;

    // Canonical brand site root so this org @id matches every other emitter and
    // dedups to one node — never the runtime document.URL. See EPS-3951.
    const siteUrl = getBrandSiteUrl(this.env.get.brandConfig.url);
    const organizationName = this.env.get.brandConfig.name ?? '';
    if (!organizationName) return;

    const done = this.pendingTasks.add();

    // getBrandStarsRating() bounds its upstream call with a 3s axios timeout. This race is
    // defense-in-depth: if that contract regresses, a hung promise would hold PendingTasks
    // open and block the SSR render, so done() must always fire (matches TopSectionComponent).
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<null>((resolve) => {
      timeoutHandle = setTimeout(() => resolve(null), TRUSTPILOT_SSR_TIMEOUT_MS);
    });

    Promise.race([this.tps.getBrandStarsRating(), timeout])
      .then((rating) => {
        if (!rating) {
          return; // timed out — Org schema falls back to the universal bare node
        }
        this.schemaService.register(
          getTrustpilotSchema({
            siteUrl,
            organizationName,
            trustScore: rating?.score?.trustScore,
            reviewCount: rating?.numberOfReviews?.total,
          }),
        );
      })
      .catch(() => {
        // Trustpilot data unavailable — Org schema will fall back to the universal
        // bare node registered by template-single's finalize().
      })
      .finally(() => {
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
        }
        done();
      });
  }

  ngAfterViewInit(): void {
    if (!this.data.showHideTrustpilot) return;
    // If trustpilot is meant to load lazily,
    // wait until this component runs in the browser to set up an intersection observer.
    // If trustpilot is not meant to load lazily, simply load the script using the script loader service.
    if (this.lazy) {
      this.tps.loadTrustpilot(this.container);
    } else if (!this.lazy) {
      this.sls.injectScriptByType('trustpilot');
    }
  }
}
